import type { TestSignalProposalToolOutput } from "@repo/app-types/entities/configurationAgent";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { renderPlural } from "@repo/ui/helpers/helpers";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { Button as AriaButton } from "react-aria-components";

export type SignalTestResultPillState =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "complete";
      data: TestSignalProposalToolOutput;
      completedAt: DateTime;
    };

export interface SignalTestResultPillProps {
  state: SignalTestResultPillState;
  onViewMatches?: () => void;
}

export const SignalTestResultPill: FC<SignalTestResultPillProps> = ({
  state,
  onViewMatches,
}) => {
  if (state.status === "loading") {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-indigo-50 px-4 py-2.5 text-sm text-indigo-700">
        <Loader style={LoaderStyle.SPINNER} />
        <span>Testing signal against customer interactions…</span>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-700">
        <IconAlertTriangle className="h-4 w-4 shrink-0" />
        <span>An error occurred. Please try again.</span>
      </div>
    );
  }

  const { summary, completedAt } = {
    summary: state.data.summary,
    completedAt: state.completedAt,
  };
  const relativeTime = completedAt.toRelative() ?? "just now";

  return (
    <AriaButton
      onPress={onViewMatches ? () => void onViewMatches() : undefined}
      isDisabled={summary.total_matches === 0}
      className="flex w-full items-center justify-between gap-2 rounded-lg bg-indigo-50 px-4 py-2.5 text-left text-sm text-indigo-700 data-[disabled]:cursor-default data-[disabled]:opacity-80 data-[hovered]:bg-indigo-100"
    >
      <span>
        <span className="font-semibold underline decoration-indigo-300 underline-offset-2">
          {summary.total_matches}{" "}
          {renderPlural(summary.total_matches, {
            one: "match",
            other: () => "matches",
          })}
        </span>{" "}
        <span className="text-muted-foreground">
          from {summary.total_interactions} sampled{" "}
          {renderPlural(summary.total_interactions, {
            one: "call",
            other: () => "calls",
          })}
        </span>
      </span>
      <span className="text-xs text-muted-foreground">Ran {relativeTime}</span>
    </AriaButton>
  );
};
